<template>
  <div class="flex items-center justify-center min-h-screen bg-cover bg-center relative p-4"
    :style="{ backgroundImage: `url(${wallpaperUrl})` }">
    <div class="bg-white/90 backdrop-blur-sm text-white p-6 sm:p-8 rounded-lg shadow-lg w-full max-w-md glass-effect-modern">
      <h1 class="text-xl sm:text-2xl font-bold text-center mb-4 sm:mb-6">Welcome!</h1>
      <form @submit.prevent="handleSubmit">
        <div class="mb-4">
          <label for="emailOrUsername" class="block text-sm font-medium mb-2">Email or Username</label>
          <input type="text" id="emailOrUsername" v-model="emailOrUsername" required
            placeholder="Enter your email or username"
            class="w-full px-4 py-3 sm:py-2 border text-white bg-transparent border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#12121c] text-base sm:text-sm min-h-[44px] touch-manipulation" />
        </div>
        <div class="mb-6">
          <label for="password" class="block text-sm font-medium mb-2">Password</label>
          <div class="relative">
            <input :type="showPassword ? 'text' : 'password'" id="password" v-model="password"
              placeholder="Enter your password" required
              class="w-full px-4 py-3 sm:py-2 pr-12 border text-white bg-transparent border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#12121c] text-base sm:text-sm min-h-[44px] touch-manipulation" />
            <button type="button" @click="showPassword = !showPassword"
              class="absolute cursor-pointer inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-[#12121c] touch-manipulation min-w-[44px] min-h-[44px] justify-center">
              <Eye v-if="!showPassword" class="w-5 h-5" />
              <EyeOff v-else class="w-5 h-5" />
            </button>
          </div>
        </div>
        <div v-if="error" class="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {{ error }}
        </div>

        <button type="submit" :disabled="isPending"
          class="cursor-pointer w-full py-3 sm:py-2 bg-[#12121c] text-white font-semibold rounded-lg hover:bg-black transition disabled:opacity-50 flex items-center justify-center min-h-[44px] text-base sm:text-sm touch-manipulation">
          <template v-if="isPending">
            <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
              viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
            <span class="ml-2">Signing in...</span>
          </template>
          <template v-else>
            Sign In
          </template>
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { Eye, EyeOff } from "lucide-vue-next";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import wallpaperImg from '../assets/images/background.png';

const wallpaperUrl = ref(wallpaperImg);
const emailOrUsername = ref("");
const password = ref("");
const showPassword = ref(false);
const error = ref(null);
const isPending = ref(false);
const router = useRouter();
const authStore = useAuthStore();

onMounted(() => {
  if (authStore.isAuthenticated) {
    router.push({ name: "Hero" });
  }
});

const validateInput = () => {
  if (!emailOrUsername.value.trim()) {
    error.value = "Email or username is required.";
    return false;
  }
  if (!password.value.trim()) {
    error.value = "Password is required.";
    return false;
  }
  if (password.value.length < 6) {
    error.value = "Password must be at least 6 characters long.";
    return false;
  }
  return true;
};

const handleSubmit = async () => {
  if (isPending.value) return; // Prevent multiple submissions
  if (!validateInput()) return;

  error.value = null;
  isPending.value = true;

  try {
    const response = await authStore.signIn(emailOrUsername.value, password.value);
    if (response.success && response.user?.uid) {
      // Clear form
      emailOrUsername.value = "";
      password.value = "";

      // Redirect to dashboard/hero
      router.push({ name: "Hero" });
    } else {
      throw new Error(response.error || "Sign-in failed");
    }
  } catch (err) {
    console.error("SignIn error:", err);
    error.value = mapFirebaseError(err.message || err.code || "unknown-error");
  } finally {
    isPending.value = false;
  }
};

const mapFirebaseError = (errorMessage) => {
  const errorCode = errorMessage.match(/auth\/[a-z-]+/)?.[0] || errorMessage;
  const errorMap = {
    "auth/invalid-email": "The email address is not valid.",
    "auth/user-not-found": "No account found with this email or username.",
    "auth/wrong-password": "Incorrect password. Please try again.",
    "auth/too-many-requests": "Too many attempts. Please try again later.",
    "auth/user-disabled": "This account has been disabled.",
    "auth/invalid-credential": "Invalid email/username or password.",
    "auth/network-request-failed": "Network error. Please check your connection.",
    "unknown-error": "An unexpected error occurred. Please try again.",
  };
  return errorMap[errorCode] || "An unexpected error occurred. Please try again.";
};
</script>

<style scoped>
.glass-effect-modern {
  background: rgba(255, 255, 255, 0.048);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.1);
}
</style>